import type { Customer, LeadGroupSummary, PipelineHistoryResponse } from './types';

function resolveBaseUrl() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  if (envUrl) {
    try {
      const parsed = new URL(envUrl);
      // If the site is served over HTTPS but the configured API URL is HTTP to the same host,
      // upgrade to HTTPS to avoid mixed-content/CSP issues.
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && parsed.protocol === 'http:') {
        parsed.protocol = 'https:';
        return parsed.origin.replace(/\/$/, '');
      }
      return envUrl.replace(/\/$/, '');
    } catch (_err) {
      return envUrl.replace(/\/$/, '');
    }
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return '';
}

const baseUrl = resolveBaseUrl();
let access = '';
let refresh: string | null = null;
let unauthorizedHandler: (() => void) | null = null;
let tokenListener: ((access: string, refresh: string | null) => void) | null = null;
let refreshPromise: Promise<boolean> | null = null;

function notifyTokenChange() {
  if (tokenListener) {
    tokenListener(access, refresh);
  }
}

export function setTokens(accessToken: string, refreshToken?: string | null) {
  access = accessToken || '';
  if (typeof refreshToken !== 'undefined') {
    refresh = refreshToken ? refreshToken : null;
  }
  notifyTokenChange();
}

export function setToken(t: string) {
  setTokens(t);
}

export function clearTokens() {
  access = '';
  refresh = null;
  notifyTokenChange();
}

export function setTokenListener(listener: ((access: string, refresh: string | null) => void) | null) {
  tokenListener = listener;
  if (listener) {
    listener(access, refresh);
  }
}

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

async function requestRefresh(): Promise<boolean> {
  if (!refresh) {
    return false;
  }

  if (!refreshPromise) {
    const refreshPath = '/api/auth/refresh';
    const refreshUrl = baseUrl ? `${baseUrl}${refreshPath}` : refreshPath;
    const payload = JSON.stringify({ token: refresh });
    const headers = new Headers({ 'Content-Type': 'application/json' });

    refreshPromise = fetch(refreshUrl, {
      method: 'POST',
      headers,
      body: payload
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('refresh failed');
        }
        const data = await response.json().catch(() => ({}));
        const newAccess = typeof data.access === 'string' ? data.access : '';
        if (!newAccess) {
          throw new Error('missing access token');
        }
        setTokens(newAccess);
        return true;
      })
      .catch(() => {
        clearTokens();
        return false;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  try {
    return await refreshPromise;
  } catch {
    return false;
  }
}

export async function api(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers || {});
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const isAbsolute = /^https?:\/\//i.test(path);
  const url = isAbsolute ? path : `${baseUrl}${path}`;
  const isRefreshEndpoint = /\/api\/auth\/refresh$/.test(url);

  async function performRequest(allowRetry: boolean): Promise<Response> {
    const attemptHeaders = new Headers(init.headers || {});
    if (access) {
      attemptHeaders.set('Authorization', `Bearer ${access}`);
    }
    const response = await fetch(url, { ...init, headers: attemptHeaders });

    if (response.status !== 401) {
      return response;
    }

    if (!allowRetry || isRefreshEndpoint) {
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
      return response;
    }

    const refreshed = await requestRefresh();
    if (!refreshed) {
      if (unauthorizedHandler) {
        unauthorizedHandler();
      }
      return response;
    }

    return performRequest(false);
  }

  return performRequest(true);
}

export async function fetchLeadGroups(): Promise<LeadGroupSummary[]> {
  const response = await api('/api/lead-groups');
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente samlekunder');
  }
  const groups = Array.isArray((data as any)?.groups) ? (data as any).groups : [];
  return groups as LeadGroupSummary[];
}

export async function fetchPipelineHistory(days: number): Promise<PipelineHistoryResponse> {
  const normalizedDays = [7, 14, 30].includes(days) ? days : 30;
  const params = new URLSearchParams({ days: String(normalizedDays) });
  const response = await api(`/api/leads/pipeline-history?${params.toString()}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke hente pipelinehistorik');
  }
  const payload = data as PipelineHistoryResponse;
  return {
    ...payload,
    points: Array.isArray(payload.points) ? payload.points : []
  };
}

type CreateCustomerDraftPayload = {
  company_name: string;
  cvrnummer: string;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  industry?: string | null;
  annual_value?: number | null;
  commission_percent?: number | null;
  commission_amount?: number | null;
  currency?: string | null;
  contract_signed_at?: string | null;
  contract_starts_at?: string | null;
  contract_expires_at?: string | null;
  renewal_notice_at?: string | null;
  notes?: string | null;
  cvr_links?: Array<{
    cvrnummer: string;
    annual_value?: number | null;
    commission_percent?: number | null;
    commission_amount?: number | null;
    currency?: string | null;
    contract_signed_at?: string | null;
    contract_starts_at?: string | null;
    contract_expires_at?: string | null;
    renewal_notice_at?: string | null;
    notes?: string | null;
  }>;
};

export const createCustomerDraftFromLead = async (leadId: string | number, payload: CreateCustomerDraftPayload) => {
  const response = await api(`/api/customers/from-lead/${leadId}/draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as any)?.error || 'Kunne ikke oprette kunde-kladde');
  }
  return data as Customer;
};